import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FilesInstanceInput,
  FilesReconcileSummaryDto,
  FilesSettingsDto,
  FilesStatusDto,
} from '@cpm/shared';
import { RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Spinner,
} from '../components/ui.js';

/**
 * Files module page (G19, ADR-0032): the SFTPGo instance registry (one per
 * Coolify server, primary-first), a "reconcile now" action, and the last
 * reconcile summary. Admins manage instances; the SFTPGo admin password is a
 * write-only secret. Per-subscription file access is managed on the
 * subscription detail page.
 */

const EMPTY: FilesInstanceInput = {
  serverUuid: '',
  adminBaseUrl: '',
  adminUsername: '',
  adminPassword: '',
  sftpHost: '',
  sftpPort: 2022,
  webClientUrl: '',
};

function InstanceForm({
  existing,
  onDone,
}: {
  existing: FilesStatusDto['instances'];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FilesInstanceInput>(EMPTY);
  const set = (patch: Partial<FilesInstanceInput>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () => {
      // Merge/replace by serverUuid, keeping the other instances' stored secrets
      // (omit adminPassword to preserve them).
      const others: FilesInstanceInput[] = existing
        .filter((i) => i.serverUuid !== form.serverUuid)
        .map((i) => ({
          serverUuid: i.serverUuid,
          adminBaseUrl: i.adminBaseUrl,
          adminUsername: i.adminUsername,
          sftpHost: i.sftpHost,
          sftpPort: i.sftpPort,
          webClientUrl: i.webClientUrl,
        }));
      return api.put<FilesSettingsDto>('/api/files/settings', {
        instances: [...others, form],
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['files', 'status'] });
      setForm(EMPTY);
      onDone();
    },
  });

  const field = (
    key: keyof FilesInstanceInput,
    label: string,
    props: React.InputHTMLAttributes<HTMLInputElement> = {},
  ) => (
    <div>
      <Label htmlFor={`files-${key}`}>{label}</Label>
      <Input
        id={`files-${key}`}
        value={key === 'sftpPort' ? form.sftpPort : (form[key] as string) ?? ''}
        onChange={(e) =>
          set({ [key]: key === 'sftpPort' ? Number(e.target.value) : e.target.value } as Partial<FilesInstanceInput>)
        }
        {...props}
      />
    </div>
  );

  return (
    <form
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {field('serverUuid', 'Coolify server UUID', { required: true, placeholder: 'srv-…' })}
      {field('adminBaseUrl', 'SFTPGo admin URL', {
        required: true,
        type: 'url',
        placeholder: 'https://files.example.com',
      })}
      {field('adminUsername', 'Admin username', { required: true })}
      {field('adminPassword', 'Admin password', {
        type: 'password',
        placeholder: 'leave blank to keep current',
      })}
      {field('sftpHost', 'Public SFTP host', { required: true, placeholder: 'sftp.example.com' })}
      {field('sftpPort', 'SFTP port', { type: 'number', min: 1, max: 65535 })}
      {field('webClientUrl', 'WebClient URL', {
        required: true,
        type: 'url',
        placeholder: 'https://files.example.com',
      })}
      {save.error != null && (
        <div className="sm:col-span-2">
          <ErrorNote error={save.error} />
        </div>
      )}
      <div className="sm:col-span-2 flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Save instance
        </Button>
      </div>
    </form>
  );
}

export function FilesPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [summary, setSummary] = useState<FilesReconcileSummaryDto | null>(null);
  const isAdmin = me.data?.role === 'admin';

  const status = useQuery({
    queryKey: ['files', 'status'],
    queryFn: () => api.get<FilesStatusDto>('/api/files/status'),
  });

  const remove = useMutation({
    mutationFn: (serverUuid: string) => {
      const instances = (status.data?.instances ?? [])
        .filter((i) => i.serverUuid !== serverUuid)
        .map((i) => ({
          serverUuid: i.serverUuid,
          adminBaseUrl: i.adminBaseUrl,
          adminUsername: i.adminUsername,
          sftpHost: i.sftpHost,
          sftpPort: i.sftpPort,
          webClientUrl: i.webClientUrl,
        }));
      return api.put('/api/files/settings', { instances });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['files', 'status'] }),
  });

  const reconcile = useMutation({
    mutationFn: () => api.post<FilesReconcileSummaryDto>('/api/files/reconcile', {}),
    onSuccess: (s) => {
      setSummary(s);
      void queryClient.invalidateQueries({ queryKey: ['files', 'status'] });
    },
  });

  if (status.isPending) return <Spinner />;
  if (status.error) return <ErrorNote error={status.error} />;
  const data = status.data;
  const last = summary ?? data.lastReconcile;

  return (
    <div>
      <PageHeader
        title="File access"
        description="SFTPGo instances that give tenants SFTP & WebClient access to resource volumes"
        actions={
          data.configured ? (
            <Button variant="secondary" loading={reconcile.isPending} onClick={() => reconcile.mutate()}>
              <RefreshCw className="mr-1 size-4" /> Reconcile now
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardHeader
          title="SFTPGo instances"
          description="One instance per Coolify server (primary-first)"
          actions={
            isAdmin ? (
              <Button variant="secondary" onClick={() => setShowForm((v) => !v)}>
                {showForm ? 'Cancel' : 'Add / edit instance'}
              </Button>
            ) : undefined
          }
        />
        <div className="px-5 pb-5">
          {data.instances.length === 0 ? (
            <p className="py-3 text-sm text-slate-500 dark:text-slate-400">
              No SFTPGo instance registered yet.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.instances.map((inst) => (
                <li key={inst.serverUuid} className="flex flex-wrap items-center gap-2 py-3 text-sm">
                  <span className="font-medium">{inst.serverName ?? inst.serverUuid}</span>
                  <Badge tone={inst.hasSecret ? 'green' : 'yellow'}>
                    {inst.hasSecret ? 'configured' : 'no password'}
                  </Badge>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {inst.sftpHost}:{inst.sftpPort}
                  </span>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => {
                        if (confirm(`Remove the SFTPGo instance for ${inst.serverUuid}?`)) {
                          remove.mutate(inst.serverUuid);
                        }
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {remove.error != null && <ErrorNote error={remove.error} />}
          {showForm && isAdmin && (
            <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
              <InstanceForm existing={data.instances} onDone={() => setShowForm(false)} />
            </div>
          )}
        </div>
      </Card>

      {last && (
        <Card className="mt-4">
          <CardHeader title="Last reconcile" />
          <div className="px-5 pb-5 text-sm text-slate-600 dark:text-slate-300">
            {new Date(last.ranAt).toLocaleString()} · {last.usersChecked} checked ·{' '}
            {last.usersRepaired} repaired · {last.errors} errors ({last.durationMs} ms)
          </div>
        </Card>
      )}
      {reconcile.error != null && <ErrorNote error={reconcile.error} />}
    </div>
  );
}
